import { useId, useMemo, useRef, useState } from 'react';
import {
  Award,
  Baby,
  Briefcase,
  Calendar,
  Camera,
  Car,
  CheckCircle,
  Clock,
  Coffee,
  DollarSign,
  Dog,
  Droplet,
  Dumbbell,
  Flame,
  Gift,
  Hammer,
  Heart,
  Home,
  Key,
  Leaf,
  Lightbulb,
  Mail,
  MapPin,
  Paintbrush,
  Phone,
  Scissors,
  Search,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Star,
  Tag,
  ThumbsUp,
  Truck,
  Users,
  Utensils,
  Wrench,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import {
  CURATED_ICONS,
  lucideIconValue,
  parseIconValue,
  type IconEntry,
} from '@pageboss/shared';
import { Popover } from '@/components/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// =====================================================================
// IconPicker — popover-anchored grid for choosing a lucide icon.
// Operator-facing surface for services.items[].icon and
// credentials.items[].icon. Backwards-compatible with emoji and
// legacy free-form values: those render as text, and the picker
// exposes a "Use emoji" text input as the explicit fallback path.
// =====================================================================

const ICON_COMPONENTS: Record<string, LucideIcon> = {
  'shield-check': ShieldCheck,
  award: Award,
  star: Star,
  'thumbs-up': ThumbsUp,
  'check-circle': CheckCircle,
  heart: Heart,
  sparkles: Sparkles,
  clock: Clock,
  zap: Zap,
  'map-pin': MapPin,
  phone: Phone,
  mail: Mail,
  calendar: Calendar,
  truck: Truck,
  tag: Tag,
  gift: Gift,
  'dollar-sign': DollarSign,
  wrench: Wrench,
  hammer: Hammer,
  paintbrush: Paintbrush,
  scissors: Scissors,
  car: Car,
  home: Home,
  leaf: Leaf,
  coffee: Coffee,
  utensils: Utensils,
  'shopping-bag': ShoppingBag,
  camera: Camera,
  dumbbell: Dumbbell,
  baby: Baby,
  dog: Dog,
  briefcase: Briefcase,
  droplet: Droplet,
  flame: Flame,
  users: Users,
  lightbulb: Lightbulb,
  key: Key,
};

const CATEGORY_LABELS: Record<IconEntry['category'], string> = {
  trust: 'Trust',
  service: 'Service',
  industry: 'Industry',
  general: 'General',
};

const CATEGORY_ORDER: IconEntry['category'][] = ['trust', 'service', 'industry', 'general'];

interface IconPickerProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  helperText?: string;
}

export function IconPicker({ label, value, onChange, helperText }: IconPickerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [search, setSearch] = useState('');
  const fieldId = useId();

  const parsed = parseIconValue(value);
  const selectedLucide = parsed?.kind === 'lucide' ? parsed.value : null;
  const SelectedIcon = selectedLucide ? ICON_COMPONENTS[selectedLucide] : null;
  // Detect a stored `lucide:foo` whose name dropped out of the curated
  // set (or never existed). Treat as empty rather than rendering the
  // literal "lucide:foo" string in the trigger / emoji input.
  const isOrphanLucide =
    parsed?.kind === 'text' && typeof value === 'string' && value.startsWith('lucide:');
  const emojiInputValue =
    parsed?.kind === 'text' && !isOrphanLucide ? parsed.value : '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return CURATED_ICONS;
    return CURATED_ICONS.filter(
      (e) =>
        e.name.includes(q) ||
        e.label.toLowerCase().includes(q) ||
        e.tags.some((t) => t.includes(q)),
    );
  }, [search]);

  const grouped = useMemo(() => {
    const groups: Record<IconEntry['category'], IconEntry[]> = {
      trust: [],
      service: [],
      industry: [],
      general: [],
    };
    for (const e of filtered) groups[e.category].push(e);
    return groups;
  }, [filtered]);

  const openPicker = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAnchorRect(rect);
    setOpen(true);
  };

  const pick = (name: string) => {
    onChange(lucideIconValue(name));
    setOpen(false);
    setSearch('');
  };

  const setEmoji = (next: string) => {
    onChange(next);
  };

  const clear = () => {
    onChange('');
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-sm font-medium text-foreground">
        {label}
      </label>
      <div className="flex items-stretch gap-2">
        <button
          id={fieldId}
          ref={triggerRef}
          type="button"
          onClick={openPicker}
          className={cn(
            'shrink-0 inline-flex items-center justify-center',
            'h-9 w-9 rounded-[var(--radius)]',
            'bg-card border border-border',
            'text-text-secondary hover:text-foreground hover:border-border-strong',
            'focus-visible:outline-2 focus-visible:outline-brand-500',
            'focus-visible:outline-offset-1 focus-visible:border-brand-500',
            'transition-colors duration-[var(--duration-fast)]',
          )}
          aria-label={selectedLucide ? `Icon: ${selectedLucide}` : 'Pick icon'}
        >
          {SelectedIcon ? (
            <SelectedIcon className="h-4 w-4 text-brand-700" aria-hidden />
          ) : parsed?.kind === 'text' && !isOrphanLucide ? (
            <span className="text-base leading-none">{parsed.value}</span>
          ) : (
            <span className="text-xs text-text-tertiary">+</span>
          )}
        </button>
        <Input
          size="sm"
          type="text"
          value={emojiInputValue}
          onChange={(e) => setEmoji(e.target.value)}
          placeholder="Or paste an emoji"
          className="flex-1 min-w-0"
        />
      </div>
      {helperText ? <p className="text-xs text-text-tertiary">{helperText}</p> : null}

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRect={anchorRect}
        placement="below"
        className="w-[20rem] max-h-[24rem] flex flex-col"
      >
        <div className="p-2 border-b border-border-subtle">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary"
              aria-hidden
            />
            <Input
              size="sm"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search icons"
              autoFocus
              className="pl-8"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-text-tertiary">
              No icons match "{search}".
            </p>
          ) : (
            CATEGORY_ORDER.map((cat) => {
              const entries = grouped[cat];
              if (entries.length === 0) return null;
              return (
                <div key={cat} className="mb-3 last:mb-0">
                  <h4 className="px-1 mb-1 eyebrow">
                    {CATEGORY_LABELS[cat]}
                  </h4>
                  <div className="grid grid-cols-6 gap-1">
                    {entries.map(({ name, label: tooltip }) => {
                      const Icon = ICON_COMPONENTS[name];
                      if (!Icon) return null;
                      const active = name === selectedLucide;
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => pick(name)}
                          title={tooltip}
                          aria-label={tooltip}
                          aria-pressed={active}
                          className={cn(
                            'inline-flex items-center justify-center h-9 w-9 rounded-[var(--radius-sm)]',
                            'transition-colors duration-[var(--duration-fast)]',
                            'focus-visible:outline-2 focus-visible:outline-brand-500',
                            active
                              ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-500'
                              : 'text-text-secondary hover:text-foreground hover:bg-surface-sunken',
                          )}
                        >
                          <Icon className="h-4 w-4" aria-hidden />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {value ? (
          <div className="p-2 border-t border-border-subtle">
            <button
              type="button"
              onClick={clear}
              className={cn(
                'inline-flex items-center gap-1.5 px-2 py-1 rounded',
                'text-xs text-text-secondary hover:text-danger-fg hover:bg-danger-bg',
                'focus-visible:outline-2 focus-visible:outline-brand-500',
              )}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Clear icon
            </button>
          </div>
        ) : null}
      </Popover>
    </div>
  );
}

// Inline helper: render the current icon at a given size for use
// outside the picker (e.g. preview chrome).
interface IconPreviewProps {
  value: string;
  className?: string;
}

export function IconPreview({ value, className }: IconPreviewProps) {
  const parsed = parseIconValue(value);
  if (!parsed) return null;
  if (parsed.kind === 'lucide') {
    const Icon = ICON_COMPONENTS[parsed.value];
    if (!Icon) return null;
    return <Icon className={className} aria-hidden />;
  }
  // Orphan `lucide:...` strings end up here as text — don't render
  // the literal value, keep the preview empty so the editor surfaces
  // the missing-icon state cleanly.
  if (typeof value === 'string' && value.startsWith('lucide:')) return null;
  return <span className={className}>{parsed.value}</span>;
}
